import { useEffect, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { formatUtcDate } from '../../utils/dateTime';

/**
 * One-shot celebration for the moment a license crosses into compliance:
 * a confetti burst rising from the hero progress bar (contained to the card)
 * plus a congratulation line announcing what was achieved. The line persists
 * as confirmation; the burst never refires. Under prefers-reduced-motion the
 * particles and pops are skipped and the message simply appears.
 */

export const EASE_OUT_QUART = 'cubic-bezier(0.25, 1, 0.5, 1)';

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0% { transform: scale(0); }
  62% { transform: scale(1.28); }
  100% { transform: scale(1); }
`;

/** One-time scale pop for a status chip flipping into its achieved state. */
export const chipPop = keyframes`
  0% { transform: scale(0.6); }
  60% { transform: scale(1.12); }
  100% { transform: scale(1); }
`;

const flashFade = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

/**
 * The small sub-goal moment: a success-tint wash over a requirement tile that
 * fades out. Purely decorative (the chip carries the state change), so callers
 * skip rendering it under prefers-reduced-motion. Host tile needs position:
 * relative.
 */
export function SubGoalFlash() {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        borderRadius: 1,
        bgcolor: 'status.completed.bg',
        pointerEvents: 'none',
        animation: `${flashFade} 1s ${EASE_OUT_QUART} 0.1s both`,
      }}
    />
  );
}

const BURST_DURATION_MS = 2600;
const PARTICLE_COUNT = 80;
/** Where the burst originates vertically: roughly the hero progress bar. */
const ORIGIN_Y_RATIO = 0.4;

interface ConfettiParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  spin: number;
  size: number;
  color: string;
  circle: boolean;
}

/** Canvas confetti scoped to the nearest positioned ancestor (the license card). */
function ConfettiBurst() {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const width = rect.width;
    const height = rect.height;

    const colors = [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.success.light,
      theme.palette.secondary.main,
      theme.palette.warning.light,
    ];

    const particles: ConfettiParticle[] = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: width * (0.08 + Math.random() * 0.84),
      y: height * ORIGIN_Y_RATIO,
      vx: (Math.random() - 0.5) * 190,
      vy: -(120 + Math.random() * 240),
      rotation: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 12,
      size: 4 + Math.random() * 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      circle: Math.random() < 0.25,
    }));

    const start = performance.now();
    let last = start;
    let frame = 0;

    const tick = (now: number) => {
      const elapsed = now - start;
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      ctx.clearRect(0, 0, width, height);
      if (elapsed >= BURST_DURATION_MS) return;

      const fadeStart = BURST_DURATION_MS * 0.6;
      const alpha =
        elapsed > fadeStart ? 1 - (elapsed - fadeStart) / (BURST_DURATION_MS - fadeStart) : 1;

      for (const p of particles) {
        p.vy += 360 * dt; // gravity
        p.vx *= 0.99;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rotation += p.spin * dt;

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        if (p.circle) {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.size / 2, -p.size * 0.35, p.size, p.size * 0.7);
        }
        ctx.restore();
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [theme]);

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  );
}

/** Copy varies so a CPA with licenses in several states never sees the same line twice in a row. */
const MESSAGE_VARIANTS: ReadonlyArray<(state: string, periodEnd: string) => string> = [
  (state, periodEnd) =>
    `That's every ${state} requirement met. You're compliant through ${periodEnd}.`,
  (state, periodEnd) => `${state} is fully covered. Nothing more due before ${periodEnd}.`,
  (state) => `Goal reached: your ${state} requirements are complete for this reporting period.`,
];

interface ComplianceCelebrationProps {
  stateName: string;
  periodEnd: string;
}

export default function ComplianceCelebration({ stateName, periodEnd }: ComplianceCelebrationProps) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // Lazy state, not useMemo: the variant is picked once at random and must
  // never change for the lifetime of this celebration.
  const [message] = useState(() =>
    MESSAGE_VARIANTS[Math.floor(Math.random() * MESSAGE_VARIANTS.length)](
      stateName,
      formatUtcDate(periodEnd)
    )
  );
  // Live regions only announce mutations after mount, so the text is injected
  // one commit later; visually imperceptible, but screen readers announce it.
  const [announce, setAnnounce] = useState(false);
  useEffect(() => {
    setAnnounce(true);
  }, []);

  // The celebration often fires while the user is elsewhere on the page (e.g.
  // right after saving a credit in a section further down), so bring the
  // achieved goal into view — otherwise the one-shot burst plays unseen.
  const messageRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = messageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const fullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (!fullyVisible) {
      el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    }
    // Mount-only: reducedMotion is fixed for the celebration's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!reducedMotion && <ConfettiBurst />}
      <Box
        ref={messageRef}
        role="status"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mt: 1.25,
          animation: reducedMotion ? undefined : `${riseIn} 0.7s ${EASE_OUT_QUART} both`,
        }}
      >
        <CheckCircleRoundedIcon
          sx={{
            fontSize: 20,
            color: 'status.completed.fg',
            animation: reducedMotion ? undefined : `${popIn} 0.6s ${EASE_OUT_QUART} both 0.2s`,
          }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'status.completed.fg' }}>
          {announce ? message : ''}
        </Typography>
      </Box>
    </>
  );
}
